import { z } from 'zod';
import { idParamsSchema, usernameSchema } from "./common.js";
import { gameResultSchema, platformSchema, timeClassSchema } from "./games.js";
export const stakeUnitSchema = z.enum(['minutes', 'points']);
export const MAX_STAKE_AMOUNT = 1000;
export const stakeSchema = z.object({
    amount: z.number().int().min(1).max(MAX_STAKE_AMOUNT),
    unit: stakeUnitSchema,
});
/** `rating-tiered` multiplies the stake by the result's rating tier (ADR-0017). */
export const stakePolicyKindSchema = z.enum(['fixed', 'rating-tiered']);
export const stakePolicySchema = z.object({
    kind: stakePolicyKindSchema,
    /** The stake itself, or the base stake a tier multiplies. */
    stake: stakeSchema,
});
/**
 * One band of the loser's rating minus the winner's, inclusive; null means unbounded. Sent by
 * the server so the UI explains the bands the server actually applies.
 */
export const ratingTierSchema = z.object({
    minGap: z.number().int().nullable(),
    maxGap: z.number().int().nullable(),
    multiplier: z.number().positive(),
});
export const stakePolicyResponseSchema = stakePolicySchema.extend({
    /** The bands that apply; empty for a fixed stake. */
    tiers: z.array(ratingTierSchema),
});
export const userSummarySchema = z.object({
    id: z.string().min(1),
    displayName: z.string().min(1),
});
/** A chess account another user tracks, as it can be named in a challenge. */
export const challengeableAccountSchema = z.object({
    id: z.string().min(1),
    platform: platformSchema,
    username: z.string().min(1),
});
/** Another user, with the accounts a wager against them can name (ADR-0024). */
export const challengeableUserSchema = userSummarySchema.extend({
    accounts: z.array(challengeableAccountSchema),
});
export const userListSchema = z.object({
    users: z.array(challengeableUserSchema),
});
export const standingRuleFilterSchema = z.object({
    /** Null matches every platform, time class or opponent. */
    platform: platformSchema.nullable(),
    timeClass: timeClassSchema.nullable(),
    ratedOnly: z.boolean(),
    opponent: usernameSchema.nullable(),
});
export const MAX_RULE_NAME_LENGTH = 60;
/** Whether a game the guard covers is silenced, is the only kind that counts, or costs double. */
export const guardEffectSchema = z.enum(['block', 'allow', 'double']);
/** Monday first: how a week reads, not how `Date` numbers it. */
export const weekdaySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
/** "09:00", in the owner's time zone; what a browser's time input speaks. */
export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM');
/** "2026-12-24", a local date rather than an instant. */
export const localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
export const MAX_GAMES_PER_DAY = 50;
/**
 * One condition on a game, beyond the filters the rule already applies: when it ended in the
 * week, which days it fell on, or how many of the rule's games that day came before it.
 */
export const ruleGuardSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('weekly-window'),
        effect: guardEffectSchema,
        days: z.array(weekdaySchema).min(1),
        /** Both ends included, and `start` is never after `end`: a window over midnight is two guards. */
        start: timeOfDaySchema,
        end: timeOfDaySchema,
    }),
    z.object({
        kind: z.literal('date-range'),
        effect: guardEffectSchema,
        /** Both ends included, and `from` is never after `to`. */
        from: localDateSchema,
        to: localDateSchema,
    }),
    z.object({
        kind: z.literal('daily-cap'),
        effect: guardEffectSchema,
        /** Games of the rule that pass before the guard starts covering them. */
        gamesPerDay: z.number().int().min(1).max(MAX_GAMES_PER_DAY),
    }),
]);
export const MAX_RULE_GUARDS = 4;
/** A guard whose ends are the wrong way round says the opposite of what it reads. */
function inOrder(guard) {
    switch (guard.kind) {
        case 'weekly-window':
            return guard.start <= guard.end;
        case 'date-range':
            return guard.from <= guard.to;
        case 'daily-cap':
            return true;
    }
}
export const ruleGuardListSchema = z
    .array(ruleGuardSchema)
    .max(MAX_RULE_GUARDS)
    .refine((guards) => guards.every(inOrder), {
    message: 'a guard must not end before it starts',
});
export const createStandingRuleRequestSchema = z.object({
    name: z.string().trim().min(1).max(MAX_RULE_NAME_LENGTH),
    filter: standingRuleFilterSchema,
    policy: stakePolicySchema,
    /** Losses always accrue; wins redeem open solo debt only when this is set. */
    redeemWins: z.boolean(),
    /** What has to hold for a game to count at all, and what it then costs. */
    guards: ruleGuardListSchema,
});
export const standingRuleSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    filter: standingRuleFilterSchema,
    policy: stakePolicyResponseSchema,
    redeemWins: z.boolean(),
    guards: z.array(ruleGuardSchema),
    createdAt: z.iso.datetime(),
});
export const standingRuleListSchema = z.object({
    rules: z.array(standingRuleSchema),
});
export const wagerStatusSchema = z.enum(['proposed', 'active', 'settled', 'declined', 'cancelled']);
export const wagerActionSchema = z.enum(['accept', 'decline', 'cancel']);
/**
 * Against a registered user the opponent is one of their accounts, picked from a list; only
 * someone not using the app is named by a typed username (ADR-0024).
 */
export const proposeWagerRequestSchema = z.discriminatedUnion('opponentKind', [
    z.object({
        opponentKind: z.literal('user'),
        /** One of the proposer's own tracked accounts; the wager is on its next game. */
        trackedPlayerId: z.string().min(1),
        /** The registered user being challenged. */
        counterpartyUserId: z.string().min(1),
        /** One of that user's tracked accounts, on the same platform. */
        opponentTrackedPlayerId: z.string().min(1),
        policy: stakePolicySchema,
    }),
    z.object({
        opponentKind: z.literal('outsider'),
        trackedPlayerId: z.string().min(1),
        opponent: usernameSchema,
        policy: stakePolicySchema,
    }),
]);
export const wagerSchema = z.object({
    id: z.string().min(1),
    /** The caller's part in the wager, and what they may do with it now. */
    role: z.enum(['proposer', 'counterparty']),
    actions: z.array(wagerActionSchema),
    proposer: userSummarySchema,
    /** Null when the opponent is not a registered user. */
    counterparty: userSummarySchema.nullable(),
    platform: platformSchema,
    playerUsername: z.string().min(1),
    opponentUsername: z.string().min(1),
    policy: stakePolicyResponseSchema,
    status: wagerStatusSchema,
    createdAt: z.iso.datetime(),
    activatedAt: z.iso.datetime().nullable(),
    /** When it was declined or cancelled. */
    closedAt: z.iso.datetime().nullable(),
    settlement: z
        .object({
        /** From the proposer's side. */
        outcome: gameResultSchema,
        gameUrl: z.url(),
        playedAt: z.iso.datetime(),
    })
        .nullable(),
});
export const wagerListSchema = z.object({
    wagers: z.array(wagerSchema),
});
export const wagerActionParamsSchema = idParamsSchema.extend({
    action: wagerActionSchema,
});
/** Whom a balance or obligation is with: yourself (solo), a user, or an unregistered opponent. */
export const counterpartySchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('self') }),
    z.object({ kind: z.literal('user'), user: userSummarySchema }),
    z.object({ kind: z.literal('opponent'), name: z.string().min(1) }),
]);
export const balanceSchema = z.object({
    counterparty: counterpartySchema,
    unit: stakeUnitSchema,
    /** Positive: you owe the counterparty. Negative: they owe you. Never zero. */
    amount: z.number().int(),
});
export const obligationSchema = z.object({
    id: z.string().min(1),
    createdAt: z.iso.datetime(),
    counterparty: counterpartySchema,
    /** Positive: you owe. Negative: you are owed, or a win redeemed solo debt. */
    amount: z.number().int(),
    unit: stakeUnitSchema,
    source: z.object({
        kind: z.enum(['rule', 'wager']),
        id: z.string().min(1),
        /** The rule's name or the wager's pairing when it was recorded. */
        label: z.string().min(1),
    }),
    /** The game from the caller's side. */
    game: z.object({
        platform: platformSchema,
        url: z.url(),
        playedAt: z.iso.datetime(),
        player: z.string().min(1),
        opponent: z.string().min(1),
        result: gameResultSchema,
        playerRating: z.number().int(),
        opponentRating: z.number().int(),
    }),
});
export const ledgerSchema = z.object({
    balances: z.array(balanceSchema),
    /** The most recent obligations, newest first. */
    obligations: z.array(obligationSchema),
});
//# sourceMappingURL=stakes.js.map